import { type CSSProperties, useEffect, useState } from 'react';

/**
 * Non-dismissable modal shown when the server triggers a rollback.
 * Informs the user that a downgrade is being downloaded and will be installed.
 *
 * The Electron main process handles the actual downgrade via electron-updater
 * with allowDowngrade=true. This modal is purely informational — the user
 * cannot cancel the rollback (it's an admin-initiated emergency action).
 *
 * @param rollbackVersion - The target version to downgrade to
 * @param currentVersion - The currently running version
 *
 * @example
 * <RollbackModal rollbackVersion="0.9.5" currentVersion="1.0.0" />
 */

interface RollbackModalProps {
  rollbackVersion: string;
  currentVersion: string;
}

export function RollbackModal({ rollbackVersion, currentVersion }: RollbackModalProps) {
  const [dots, setDots] = useState('');

  // Animated dots for the "downloading" state
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : `${prev}.`));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={iconContainer}>
          <svg
            role="img"
            aria-label="Rollback in progress"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-tint-orange)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </div>

        <h2 style={title}>Probíhá návrat k předchozí verzi</h2>

        <p style={description}>
          Správce systému zahájil návrat z verze {currentVersion} na verzi {rollbackVersion}.
          Stahování probíhá automaticky{dots}
        </p>

        <div style={progressContainer}>
          <div style={progressBar}>
            <div style={progressBarFill} />
          </div>
        </div>

        <p style={helpText}>
          Aplikace se po stažení automaticky restartuje. Neukončujte prosím aplikaci.
        </p>
      </div>
    </div>
  );
}

// ─── Styles ───

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
};

const modal: CSSProperties = {
  background: 'var(--color-bg-primary, #ffffff)',
  borderRadius: 16,
  padding: 32,
  maxWidth: 420,
  width: '90%',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const iconContainer: CSSProperties = {
  marginBottom: 16,
};

const title: CSSProperties = {
  fontSize: 'var(--font-size-title2, 22px)',
  fontWeight: 700,
  color: 'var(--color-label-primary, #000)',
  margin: '0 0 8px',
};

const description: CSSProperties = {
  fontSize: 'var(--font-size-body, 15px)',
  color: 'var(--color-label-secondary, #666)',
  lineHeight: 1.5,
  margin: '0 0 24px',
};

const progressContainer: CSSProperties = {
  marginBottom: 16,
};

const progressBar: CSSProperties = {
  height: 4,
  borderRadius: 2,
  background: 'var(--color-bg-tertiary, #e5e5e5)',
  overflow: 'hidden',
};

const progressBarFill: CSSProperties = {
  height: '100%',
  borderRadius: 2,
  background: 'var(--color-tint-orange, #FF9500)',
  animation: 'rollback-progress 2s ease-in-out infinite',
  width: '40%',
};

const helpText: CSSProperties = {
  fontSize: 'var(--font-size-caption, 12px)',
  color: 'var(--color-label-tertiary, #999)',
  marginTop: 16,
  margin: '16px 0 0',
};

// Inject keyframes for the progress bar animation
if (typeof document !== 'undefined' && !document.getElementById('rollback-keyframes')) {
  const style = document.createElement('style');
  style.id = 'rollback-keyframes';
  style.textContent = `
    @keyframes rollback-progress {
      0% { transform: translateX(-100%); }
      50% { transform: translateX(150%); }
      100% { transform: translateX(-100%); }
    }
  `;
  document.head.appendChild(style);
}
